import React, { useCallback, useEffect, useMemo, useState } from "react"
import Button from "@mui/material/Button"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import Typography from "@mui/material/Typography"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import { useSnackbar } from "notistack"
import { verifyResponse } from "utils/httpRequest"
import {
  addSystemConfigs,
  editSystemConfigs,
  getSystemConfigByKey,
} from "services/systemConfig"
import { StyledAiAgent } from "components/Pages/AiAgent/AiAgent.style"

const STUDY_LAB_CONFIG_KEY = "study_lab_model_config"
const DEFAULT_CONFIG = {
  enabled: false,
  primaryModel: "gpt-5.2",
  fallbackModel: "gpt-5.2",
  backendTimeoutMs: 45000,
  retryTimeoutMs: 30000,
}

const normalizeEnabled = (value, fallback = false) => {
  if (typeof value === "boolean") return value
  const numeric = Number(value)
  if (Number.isFinite(numeric)) return numeric > 0
  const lower = String(value || "").trim().toLowerCase()
  if (!lower) return fallback
  if (["true", "yes", "on", "enabled"].includes(lower)) return true
  if (["false", "no", "off", "disabled"].includes(lower)) return false
  return fallback
}

const toConfigObject = (value) => {
  if (value && typeof value === "object") return value
  return {}
}

const AiStudyLab = () => {
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [configRecord, setConfigRecord] = useState(null)
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listAiStudyLabAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_LIST,
      ]),
      editAiStudyLabAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_EDIT,
      ]),
    })
  }, [AccessControl.UserPermissions])

  const loadConfig = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getSystemConfigByKey(STUDY_LAB_CONFIG_KEY)
      verifyResponse(response)
      const item = response?.data || null
      const configObj = toConfigObject(item?.configValueJson)
      setConfigRecord(item)
      setEnabled(normalizeEnabled(configObj.enabled ?? configObj.isEnabled, false))
    } catch {
      setConfigRecord(null)
      setEnabled(false)
      enqueueSnackbar("Unable to load Study Lab settings. Defaulting to disabled.", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const effectiveConfig = useMemo(() => {
    const fromDb = toConfigObject(configRecord?.configValueJson)
    return {
      ...DEFAULT_CONFIG,
      ...fromDb,
      enabled,
    }
  }, [configRecord?.configValueJson, enabled])

  const handleSave = async () => {
    if (!AccessControl.Access.editAiStudyLabAccess) return
    setSaving(true)
    try {
      const payload = {
        name: configRecord?.name || "Study Lab Model Config",
        configKey: STUDY_LAB_CONFIG_KEY,
        description:
          configRecord?.description ||
          "Runtime model, timeout, and availability config for Study Lab generation.",
        configValue: JSON.stringify(effectiveConfig),
      }

      if (configRecord?.id) {
        const response = await editSystemConfigs({
          ...payload,
          id: configRecord.id,
        })
        verifyResponse(response)
      } else {
        const response = await addSystemConfigs(payload)
        verifyResponse(response)
      }

      enqueueSnackbar("AI Study Lab settings saved.", { variant: "success" })
      await loadConfig()
    } catch {
      enqueueSnackbar("Failed to save AI Study Lab settings.", {
        variant: "warning",
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingScreen />

  return (
    <StyledAiAgent>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">AI Study Lab</h1>
          <p className="page-subtitle">
            Control whether AI Study Lab is visible in the course experience.
          </p>
        </div>
        <div className="actions">
          {AccessControl.Access.editAiStudyLabAccess && (
            <Button
              className="new-btn"
              variant="contained"
              size="medium"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
        </div>
      </PageHeading>

      <div className="page-surface">
        {AccessControl.Access.listAiStudyLabAccess ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Alert severity={enabled ? "success" : "warning"} variant="outlined">
              {enabled
                ? "AI Study Lab is enabled and visible in courses."
                : "AI Study Lab is disabled (default) and hidden in courses."}
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={!AccessControl.Access.editAiStudyLabAccess || saving}
                />
              }
              label={enabled ? "Enabled" : "Disabled"}
            />
            <Typography variant="body2" color="text.secondary">
              This setting is stored in system config key{" "}
              <strong>{STUDY_LAB_CONFIG_KEY}</strong> as <strong>enabled</strong>.
            </Typography>
          </Box>
        ) : (
          <div className="page-empty">
            You do not have permission to view AI Study Lab settings.
          </div>
        )}
      </div>
      <LoadingScreen loading={saving} />
    </StyledAiAgent>
  )
}

export default AiStudyLab
